<script setup lang="ts">
import { useUserStore } from '@/store/user/user'

import {
  StoryLinkClickEvent,
  StoryManager,
} from '@/services/inAppStory/inAppStories.types'

import {
  getDesktopStoriesOptions,
  getMobileStoriesOptions,
  injectStoriesScript,
} from '@/services/inAppStory/inAppStories'

const { isMobileOrTablet, isMobile } = useDevice()

const COUNT_SKELETON_STORIES = isMobileOrTablet ? 5 : 7
const SUBSTRING_INCLUDE_LINK = 'farfor_in_app_stories_include_link'

const props = defineProps({
  feed: {
    type: String,
    default: 'default',
    validate: (value: string) => ['default', 'main'].includes(value),
  },
})

const userStore = useUserStore()

onMounted(async () => {
  // Получаем статистику пользователя
  await userStore.getStatistic()

  setStories()
})

const isLoadedStories = ref(false)

const setStories = async () => {
  const storiesManagers = await injectStoriesScript()

  if (!storiesManagers) {
    console.warn(
      'Ошибка загрузки InAppStories скрипта. Не удалось создать managers',
    )
    return
  }

  const { storyManager, appearanceManager } = storiesManagers

  setAppearanceManager(appearanceManager)

  // Если тестовая среда - показываем сторисы на модерации
  const testApiKey = useRuntimeConfig().public.IN_APP_STORIES_API_KEY_TEST || ''

  const storiesList = new storyManager.StoriesList(
    '#stories_widget',
    appearanceManager,
    {
      feed: props.feed,
      testKey: testApiKey,
    },
  )

  storiesList.on('endLoad', (loaderContainer: HTMLElement) => {
    loaderContainer.style.background = 'none' // Необходимо, чтобы скрыть дефолтную анимацию
    isLoadedStories.value = true
  })

  handleLinkClick(storyManager)
}

// Визуальная настрйока сторисов
const storiesOptions = isMobileOrTablet
  ? getMobileStoriesOptions(isMobile)
  : getDesktopStoriesOptions()

const setAppearanceManager = (appearanceManager: any) => {
  // Example config
  appearanceManager
    .setCommonOptions({ hasLike: true, hasFavorite: false })
    .setStoriesListOptions(storiesOptions)
    .setStoryReaderOptions({
      scrollStyle: 'flat',
    })
}

// Обработка нажатия на ссылку в сторисах
const handleLinkClick = (storyManager: StoryManager) => {
  // https://farfor_in_app_stories_include_link/terms_of_use/
  storyManager.storyLinkClickHandler = (payload: StoryLinkClickEvent) => {
    storyManager.closeStoryReader()

    const src = payload.data.url

    const isIncludeLink = src.includes(SUBSTRING_INCLUDE_LINK)

    // Если ссылка внешняя - редирект на новую вкладку
    if (!isIncludeLink) {
      window.open(src, '_blank')
      return
    }

    const includeSrc = src.split(SUBSTRING_INCLUDE_LINK)[1]

    navigateTo(includeSrc)
  }
}
</script>

<template>
  <div class="stories-container">
    <div
      v-show="isLoadedStories"
      id="stories_widget"
      class="stories-container__widget"
    />

    <div v-if="!isLoadedStories" class="stories-container__loader">
      <the-skeleton-group :gap="isMobileOrTablet ? 8 : 16">
        <the-skeleton
          v-for="index in COUNT_SKELETON_STORIES"
          :key="index"
          :width="isMobileOrTablet ? 98 : 178"
          :height="isMobileOrTablet ? 98 : 178"
          :radius="isMobileOrTablet ? 20 : 36"
        />
      </the-skeleton-group>
    </div>
  </div>
</template>

<style scoped lang="scss">
.stories-container {
  padding: 32px 0 0 20px;

  @include media('>=desktop-mini') {
    max-width: 1240px;
    padding: 40px 20px 0;
    margin: 0 auto;
  }

  &__widget {
    width: 100% !important;

    @include media('>tablet') {
      width: calc(100% + 50px) !important;
      margin-left: -25px;
    }
  }

  &__loader {
    overflow: hidden;
  }
}
</style>
